import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaWallet, FaCheckCircle, FaClock, FaCalendarAlt, FaPhone } from 'react-icons/fa';
import { feeService } from '../../services/api';
import PaymentGateway from './PaymentGateway';
import toast from 'react-hot-toast';

const FeePayment = () => {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0 });

  useEffect(() => {
    fetchFees();
  }, []);

  const fetchFees = async () => {
    try {
      const studentId = localStorage.getItem('studentId');
      if (!studentId) {
        toast.error('Student ID not found');
        return;
      }
      const response = await feeService.getByStudent(studentId);
      const feeData = response.data.data || [];
      setFees(feeData);
      
      const total = feeData.reduce((sum, f) => sum + (f.amount || 0), 0);
      const paid = feeData.filter(f => f.status === 'paid' || f.paid).reduce((sum, f) => sum + (f.amount || 0), 0);
      const pending = total - paid;
      setStats({ total, paid, pending });
    } catch (error) {
      console.error('Error fetching fees:', error);
      toast.error('Failed to load fees');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    fetchFees();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-800">💳 Fee Payments</h1>
        <p className="text-gray-500">Pay your fees securely with PhonePe</p>
      </motion.div>

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
      >
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Total Fees</p>
              <p className="text-2xl font-bold">₹{stats.total}</p>
            </div>
            <FaWallet className="text-3xl opacity-50" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Paid</p>
              <p className="text-2xl font-bold">₹{stats.paid}</p>
            </div>
            <FaCheckCircle className="text-3xl opacity-50" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Pending</p>
              <p className="text-2xl font-bold">₹{stats.pending}</p>
            </div>
            <FaClock className="text-3xl opacity-50" />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  <FaCalendarAlt className="inline mr-2" /> Fee Type
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fees.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    No fees found
                  </td>
                </tr>
              ) : (
                fees.map((fee, index) => (
                  <tr key={fee._id || index} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <span className="font-medium">{fee.month || 'Monthly Fee'}</span>
                      <p className="text-xs text-gray-400">{fee.description || 'Tuition Fee'}</p>
                    </td>
                    <td className="px-4 py-3 font-semibold">₹{fee.amount || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${
                        fee.status === 'paid' || fee.paid 
                          ? 'bg-green-100 text-green-800' 
                          : fee.status === 'upcoming' || fee.upcoming
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {fee.status === 'paid' || fee.paid ? '✅ Paid' : 
                         fee.status === 'upcoming' || fee.upcoming ? '📅 Upcoming' : 
                         '⏳ Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {fee.status !== 'paid' && !fee.paid && (
                        <PaymentGateway
                          amount={fee.amount || 0}
                          feeId={fee._id}
                          studentId={localStorage.getItem('studentId')}
                          feeMonth={fee.month || 'Monthly Fee'}
                          onSuccess={handlePaymentSuccess}
                        />
                      )}
                      {(fee.status === 'paid' || fee.paid) && (
                        <span className="text-green-600 text-sm font-semibold flex items-center gap-1">
                          <FaCheckCircle /> Completed
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-200"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <FaPhone className="text-purple-600 text-xl" />
          </div>
          <div>
            <p className="font-semibold text-gray-800">PhonePe Payment</p>
            <p className="text-sm text-gray-600">Secure, instant, and UPI enabled payments</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FeePayment;
